/**
 * User identification utility for generating and managing a persistent UUID
 * that can be used across various SDKs including RevenueCat.
 */
import { useAuthStore } from "@/stores/useAuth";

const logger = (message: string, values?: unknown) => {
  const isDev = import.meta.env.DEV;

  if (isDev) console.warn(`[UserIdentifier]: ${message}`, values ?? {});
};

class UserIdentifier {
  /**
   * Storage key for the legacy UUID and email in localStorage
   * Storage as been moved to the auth store
   */
  static LEGACY_STORAGE_KEY = "app_user_uuid";
  static LEGACY_EMAIL_KEY = "user_email";

  /**
   * Gets the user's UUID from localStorage or generates a new one if it doesn't exist
   */
  static getUserUUID(): string {
    if (typeof window === "undefined") return "";
    const uuid = useAuthStore.getState().currentUser?.trackingId || "";

    return uuid;
  }

  /**
   * Initializes the user ID for SDKs that need it
   */
  static initializeForSDKs(): string {
    if (typeof window === "undefined") return "";

    const userUUID = this.getUserUUID();

    // Example of how to use with RevenueCat (uncomment when implementing)
    // if (typeof Purchases !== 'undefined') {
    //   Purchases.setCustomerInfo({ appUserID: userUUID });
    // }

    // Add other SDK initializations as needed

    return userUUID;
  }

  static getEmail(): string {
    if (typeof window === "undefined") return "";

    return useAuthStore.getState().currentUser?.email || "";
  }

  /**
   * Checks if the user has an email stored
   */
  static hasEmail(): boolean {
    return !!this.getEmail();
  }

  /**
   * Clears the user's email from localStorage
   */
  static clearEmail() {
    useAuthStore.setState({ email: null });
    logger("Email cleared");
  }

  /**
   * Gets all user identifiers (UUID and email) for analytics or debugging
   */
  static getUserIdentifiers(): { uuid: string; email: string } {
    if (typeof window === "undefined") return { uuid: "", email: "" };

    return {
      uuid: this.getUserUUID(),
      email: this.getEmail(),
    };
  }
}

// Initialize the user ID when the script loads, but only in browser environment
if (typeof window !== "undefined") {
  document.addEventListener("DOMContentLoaded", function () {
    UserIdentifier.initializeForSDKs();
  });
}

// Export for use in other modules
export default UserIdentifier;
